import { useEffect, useState, type RefObject } from "react";

/** Returned until the element has actually been measured. See `useElementWidth`. */
export const UNMEASURED = -1;

/**
 * The width of an element, tracked as it changes.
 *
 * The designer is embedded, so it can be any width inside any host at any
 * viewport. A media query would answer a question nobody asked — "how big is
 * the screen" — when the one that matters is "how much room have I got".
 *
 * Unmeasured is `UNMEASURED`, not `0`, because those are different facts and
 * conflating them was a real bug: a toolbar group in a `1fr` track really can
 * measure zero when the neighbouring track has taken everything, and callers
 * that read zero as "not measured yet, assume room" then laid out at their
 * widest in no room at all — off the side of the screen.
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(UNMEASURED);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver !== "function") return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}
